import { z } from 'zod';

/* One media attachment on a message. Bytes are streamed from the
   authorized download endpoint keyed on `id`; `kind` is IMAGE / AUDIO / FILE. */
export const attachmentSchema = z.object({
  id: z.string(),
  kind: z.string(),
  mime: z.string(),
  size_bytes: z.number(),
  duration_ms: z.number().nullable(),
});
export type ChatAttachment = z.infer<typeof attachmentSchema>;

/* Structured payload on an APPROVAL_REQUEST message: a geo-approval the dispatcher
   acts on with an approve/reject button. `phase` picks the order endpoint (START →
   approve-start, FINISH → approve-completion); `status` drives the button state. */
export const approvalPayloadSchema = z.object({
  order_id: z.string(),
  phase: z.enum(['START', 'FINISH']),
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED']),
  order_ref: z.string().optional(),
  car_number: z.string().optional(),
  operator_id: z.string().nullable().optional(),
  operator_name: z.string().optional(),
  distance_m: z.number().nullable().optional(),
  no_gps: z.boolean().optional(),
  fuel_amount_liters: z.union([z.string(), z.number()]).nullable().optional(),
});
export type ApprovalPayload = z.infer<typeof approvalPayloadSchema>;

export const messageSchema = z.object({
  id: z.string(),
  chat_id: z.string(),
  author_type: z.string(),
  author_id: z.string().nullable(),
  text: z.string(),
  // `TEXT` or `APPROVAL_REQUEST`. Absent on legacy payloads → default TEXT.
  kind: z.string().default('TEXT'),
  // Structured payload for actionable kinds. Parsed with `approvalPayloadSchema` at render.
  payload: z.unknown().nullable().optional(),
  sent_at: z.string(),
  read_at: z.string().nullable(),
  // Absent on legacy/text-only payloads — default to empty so callers can
  // always map over it.
  attachments: z.array(attachmentSchema).default([]),
});
export type ChatMessage = z.infer<typeof messageSchema>;

export const messagesSchema = z.array(messageSchema);

/* Dispatcher inbox row: one operator's general chat with its unread
   (operator-authored) count and last activity. Drives the left-rail
   unread badges so a dispatcher sees incoming from every operator. */
export const chatSummarySchema = z.object({
  operator_id: z.string(),
  unread: z.number(),
  last_message_at: z.string().nullable(),
});
export type ChatSummary = z.infer<typeof chatSummarySchema>;

export const chatSummariesSchema = z.array(chatSummarySchema);

/* Support inbox row: one B2C consumer's general chat (B12) — mirrors `chatSummarySchema`
   exactly, keyed on `consumer_id` instead of `operator_id`. A sibling type, not a union, since
   the two inboxes are separate backend resources (`/v1/admin/chats` vs
   `/v1/admin/consumer-chats`), never merged into one list. */
export const consumerChatSummarySchema = z.object({
  consumer_id: z.string(),
  unread: z.number(),
  last_message_at: z.string().nullable(),
});
export type ConsumerChatSummary = z.infer<typeof consumerChatSummarySchema>;

export const consumerChatSummariesSchema = z.array(consumerChatSummarySchema);
